import {
  ChangeEvent,
  createContext,
  FormEvent,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { parse } from 'date-fns';
import { get, set } from 'lodash';

import type { FormContext } from './types';
import {
  ErrorMap,
  FormProviderProps,
  InputOpts,
  RegisterFn,
  SubmitCallback,
} from './types';

const formatValue = (v: FormDataEntryValue, config?: InputOpts) => {
  if (typeof v !== 'string') return v; // mostly just for File inputs

  if (config?.nullable && v === '') return null;

  if (config?.mutations) {
    config.mutations.forEach(mutator => {
      v = mutator(v as string);
    });
  }

  switch (config?.dataType) {
    case 'boolean':
      return v === 'true';
    case 'number':
      return parseFloat(v);
    case 'date':
      if (!config.dateFormat)
        throw new Error('Must supply a format for date-type inputs');

      return parse(v, config.dateFormat, new Date());
    case 'list':
      return v.split(',');
    case 'phone':
      return v.replace(/[^0-9]/g, '');
    case 'pipeArray':
      return v.split('|').filter(v => v !== '');
    default:
      return v;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FormContext = createContext<any>(null);

export function FormProvider<T>({
  children,
  onSubmit,
  validateOnBlur,
  onFieldBlur,
  controlRef,
}: PropsWithChildren<FormProviderProps<T>>) {
  const localFormRef = useRef<HTMLFormElement>(null);
  const formRef = controlRef ?? localFormRef;

  const fields = useRef<{ [key: string]: InputOpts }>({});
  const [errors, setErrors] = useState<ErrorMap>({});

  const submitCallback = useRef(
    onSubmit ||
      (() => {
        throw new Error('No submit callback was registered');
      }),
  );

  useEffect(() => {
    if (onSubmit) {
      submitCallback.current = onSubmit;
    }
  }, [onSubmit]);

  const registerField: RegisterFn = (name: string, inputOpts?: InputOpts) => {
    const defaultInputOpts: InputOpts = { validators: [] };
    fields.current[name] = inputOpts ?? defaultInputOpts;

    return {
      name,
      error: !!errors[name],
      helperText: errors[name] || undefined,
      invalid: errors[name] ? 'true' : 'false',
      ['aria-invalid']: errors[name] ? 'true' : 'false',
      onBlur: e => {
        setErrors({ ...errors, [name]: '' });
        if (inputOpts?.validateOnBlur === false) return;

        if (!validateOnBlur && !inputOpts?.validateOnBlur) {
          return;
        }

        const value = formatValue(e.target.value, inputOpts);
        if (value === null && inputOpts?.nullable) return;

        let error: string | null = null;
        for (const v of inputOpts?.validators ?? []) {
          error = v(value);
          if (error) {
            break;
          }
        }

        if (error) {
          setErrors({
            ...errors,
            [name]: error,
          });
        } else if (onFieldBlur) {
          onFieldBlur(name, value, () => true);
        }
      },
      ...(inputOpts?.mask && {
        onChange: (e: ChangeEvent<HTMLInputElement>) => {
          if (!inputOpts.mask) return;
          e.target.value = inputOpts?.mask(e.target.value);
        },
      }),
    };
  };

  const getValues = useCallback(() => {
    if (!formRef.current) return { errors: {}, values: {}, isValid: false };

    const formData = new FormData(formRef.current);
    const formValues = {};
    const errors: ErrorMap = {};
    let isValid = true;

    for (const [path, value] of formData.entries()) {
      // creates a nested object (and proper arrays) from field names like
      // relations[1].contactDetails.name

      set(formValues, path, formatValue(value, fields.current[path]));

      for (const v of fields.current[path]?.validators ?? []) {
        const error = v(get(formValues, path));

        if (error) {
          errors[path] = error;
          break;
        }
      }

      if (errors[path]) isValid = false;
    }

    return { values: formValues as T, errors, isValid };
  }, [formRef]);

  const scrollToError = (errors: ErrorMap) => {
    for (const key of Object.keys(errors)) {
      const e = document.querySelector(`[name="${key}"]`);
      if (e) {
        e.scrollIntoView({ behavior: 'smooth' });
        return;
      }
    }
  };

  const onSubmitHandler = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      const { values, errors, isValid } = getValues();

      setErrors(errors);

      if (!isValid) {
        scrollToError(errors);
      }

      submitCallback.current(isValid, values as T);
    },
    [getValues],
  );

  const registerSubmitCallback = (fn: SubmitCallback<T>) => {
    if (onSubmit) {
      throw new Error(
        "Can't registerSubmitCallback - this Form's submission is controlled by the `onSubmit` prop on its FormProvider.",
      );
    }

    submitCallback.current = fn;
    return onSubmitHandler;
  };

  const validate = () => {
    const { errors, isValid } = getValues();
    return isValid ? null : errors;
  };

  const context = {
    registerField,
    registerSubmitCallback,
    validate,
    getValues,
  };

  return (
    <FormContext.Provider value={context}>
      <form ref={formRef} onSubmit={onSubmitHandler}>
        {children}
      </form>
    </FormContext.Provider>
  );
}

export function useForm<T>() {
  const context = useContext<FormContext<T>>(FormContext);

  if (!context)
    throw new Error('!--- useForm must be used inside a FormProvider ---!');

  return context;
}
